import { useEffect, useState } from "react";
import { toClientLinkStatusLabel } from "@enrein/shared-contracts/display/tier1-client-display";
import { ClarificationWorkspace } from "../../ClarificationWorkspace.js";
import { LaunchProofSummaryPanel } from "../../LaunchProofSummaryPanel.js";
import { SafeEditPanel } from "../../SafeEditPanel.js";
import { isIntakePhase } from "./adapters/intake-phase.js";
import { resolveClientNextActionHome } from "./adapters/next-action-home.js";
import { applyClientHubPwaBranding } from "./adapters/pwa-branding.js";
import { defaultClientTab, tabForJourneyIntent, visibleClientTabs } from "./adapters/tab-visibility.js";
import { GreenfieldClientNav, GreenfieldClientShell } from "./chrome/GreenfieldClientShell.js";
import type { GreenfieldClientHubProps, GreenfieldClientTabId } from "./types.js";
import { GreenfieldChangesView } from "./views/GreenfieldChangesView.js";
import { GreenfieldIntakeView } from "./views/GreenfieldIntakeView.js";
import { GreenfieldPreviewView } from "./views/GreenfieldPreviewView.js";
import { GreenfieldHelpView, GreenfieldRecordsView } from "./views/GreenfieldRecordsView.js";
import { GreenfieldReviewView } from "./views/GreenfieldReviewView.js";

function fallbackBranding(displayName: string) {
  return {
    schemaVersion: "client-workspace-branding.v1.0" as const,
    requirementId: "CW-001" as const,
    ok: true as const,
    businessDisplayName: displayName,
    agencyDisplayName: "Your agency",
    logoUrl: null,
    helpLabel: "Help",
  };
}

export function GreenfieldClientHubApp(props: GreenfieldClientHubProps) {
  const branding = props.shell.branding ?? fallbackBranding(props.shell.project.displayName);
  const tabIds = visibleClientTabs({ shell: props.shell, preview: props.preview });
  const intakePhase = isIntakePhase(props.shell.state.status);
  const canAct = ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING"].includes(props.shell.state.status);
  const [activeTab, setActiveTab] = useState<GreenfieldClientTabId>(() =>
    defaultClientTab({ shell: props.shell, tabIds }),
  );
  const [localPreview, setLocalPreview] = useState(props.preview);
  const [approvalBaselineVersionId] = useState(() => props.preview?.previewVersionId ?? null);
  const linkId = props.locationContext.clientLinkId;
  const intakeComplete = !intakePhase;
  const previewReady =
    Boolean(props.preview?.previewVersionId) ||
    ["PREVIEW_READY", "CHANGE_REQUESTED", "APPROVAL_PENDING", "APPROVED"].includes(props.shell.state.status);
  const nextActionHome = resolveClientNextActionHome({
    projectId: props.shell.clientLinkId,
    clientLinkStatus: props.shell.state.status,
    intakeComplete,
    previewReady,
    journeyPresentation: props.journeyPresentation ?? null,
  });

  useEffect(() => {
    setLocalPreview(props.preview);
  }, [props.preview]);

  useEffect(() => {
    applyClientHubPwaBranding({
      businessDisplayName: branding.businessDisplayName,
      logoUrl: branding.logoUrl,
    });
  }, [branding.businessDisplayName, branding.logoUrl]);

  useEffect(() => {
    if (!tabIds.includes(activeTab)) {
      setActiveTab(defaultClientTab({ shell: props.shell, tabIds }));
    }
  }, [activeTab, props.shell, tabIds]);

  const handleNextActionClick = () => {
    const next = tabForJourneyIntent(nextActionHome.presentation?.primaryAction?.intent);
    if (next && tabIds.includes(next)) {
      setActiveTab(next);
    }
  };

  return (
    <GreenfieldClientShell branding={branding}>
      <p className="ws-muted" data-testid="client-hub-status">
        {toClientLinkStatusLabel(props.shell.state.status)}
      </p>
      {nextActionHome.presentation ? (
        <section className="ws-card" data-testid="client-journey-presentation" aria-label="Your next step">
          <h2 className="ws-title" data-testid="client-journey-title">
            {nextActionHome.presentation.title}
          </h2>
          <p data-testid="client-journey-condition">{nextActionHome.presentation.condition}</p>
          {nextActionHome.presentation.relevantRecords.length > 0 ? (
            <ul data-testid="client-journey-records">
              {nextActionHome.presentation.relevantRecords.map((record) => (
                <li key={record}>{record}</li>
              ))}
            </ul>
          ) : null}
          <p data-testid="client-next-action">
            {nextActionHome.presentation.primaryAction ? (
              <button
                type="button"
                className="ws-btn ws-btn-primary"
                data-testid="client-next-action-cta"
                onClick={handleNextActionClick}
              >
                {nextActionHome.nextActionLabel}
              </button>
            ) : (
              nextActionHome.nextActionLabel
            )}
          </p>
        </section>
      ) : (
        <p data-testid="client-next-action">{nextActionHome.nextActionLabel}</p>
      )}
      <GreenfieldClientNav activeTab={activeTab} onTabChange={setActiveTab} tabIds={tabIds} />
      {activeTab === "home" ? (
        <section className="ws-card" id="gf-tab-home" role="tabpanel" aria-labelledby="gf-tab-btn-home">
          <h1 className="ws-display">My Business</h1>
          <p className="ws-muted">{props.shell.subcopy}</p>
          <p>Follow the next step above. You do not need the menu to finish this journey.</p>
        </section>
      ) : null}
      <GreenfieldIntakeView
        active={activeTab === "intake"}
        api={props.api}
        linkId={linkId}
        sessionToken={props.sessionToken}
        linkStatus={props.shell.state.status}
        onActionStatus={props.onActionStatus}
      />
      <GreenfieldPreviewView
        shell={props.shell}
        preview={localPreview}
        api={props.api}
        sessionToken={props.sessionToken}
        active={activeTab === "preview"}
        linkId={linkId}
        approvalBaselineVersionId={approvalBaselineVersionId}
        onPreviewChange={setLocalPreview}
      />
      <GreenfieldReviewView
        shell={props.shell}
        preview={localPreview}
        isExternalProof={props.isExternalProof}
        changeMessage={props.changeMessage}
        onApprove={props.onApprove}
        onRequestChange={props.onRequestChange}
        active={activeTab === "review"}
        canAct={canAct}
      />
      <GreenfieldChangesView
        changeMessage={props.changeMessage}
        setChangeMessage={props.setChangeMessage}
        api={props.api}
        deployEnv={props.deployEnv}
        sessionToken={props.sessionToken}
        active={activeTab === "changes"}
        canAct={canAct}
        onRequestChange={props.onRequestChange}
        shellStatus={props.shell.state.status}
        linkId={linkId}
        preview={localPreview}
        onOpenRevisedPreview={(previewVersionId) => {
          if (localPreview?.previewVersionId === previewVersionId) {
            setActiveTab("preview");
          }
        }}
        safeUpdateSlot={
          <SafeEditPanel
            api={props.api}
            clientLinkId={linkId}
            token={props.sessionToken}
            onStatus={props.onActionStatus}
          />
        }
      />
      <div id="gf-tab-requests" role="tabpanel" aria-labelledby="gf-tab-btn-requests" hidden={activeTab !== "requests"}>
        {activeTab === "requests" ? (
          <section className="ws-card">
            <h2 className="ws-title">Information requests</h2>
            <ClarificationWorkspace
              api={{
                listClarifications: () => props.api.listClarifications(linkId, props.sessionToken),
                respondToClarification: (clarificationRequestId, body) =>
                  props.api.respondToClarification(linkId, props.sessionToken, clarificationRequestId, body),
              }}
            />
          </section>
        ) : null}
      </div>
      <GreenfieldRecordsView
        active={activeTab === "records"}
        api={props.api}
        linkId={linkId}
        token={props.sessionToken}
        proofSlot={
          <LaunchProofSummaryPanel
            api={props.api}
            clientLinkId={linkId}
            token={props.sessionToken}
            enabled={activeTab === "records"}
          />
        }
      />
      <GreenfieldHelpView
        active={activeTab === "help"}
        agencyDisplayName={branding.agencyDisplayName}
        helpLabel={branding.helpLabel}
      />
      {props.actionStatus ? (
        <p className="ws-global-notice" role="status">
          {props.actionStatus}
        </p>
      ) : null}
    </GreenfieldClientShell>
  );
}
